import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Skill } from '../types';

interface SkillsMatrixProps {
  skills: Skill[];
}

const SkillsMatrix = ({ skills }: SkillsMatrixProps) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Skills', icon: 'Grid3X3' },
    { id: 'frontend', name: 'Frontend', icon: 'Monitor' },
    { id: 'backend', name: 'Backend', icon: 'Server' },
    { id: 'tools', name: 'Tools', icon: 'Wrench' },
    { id: 'soft', name: 'Soft Skills', icon: 'Users' },
  ];

  const filteredSkills = activeCategory === 'all'
    ? skills
    : skills.filter(skill => skill.category === activeCategory);

  const getProficiencyText = (level: number) => {
    if (level >= 4.5) return 'Master';
    if (level >= 4) return 'Expert';
    if (level >= 3) return 'Advanced';
    if (level >= 2) return 'Intermediate';
    return 'Beginner';
  };

  return (
    <section className="bg-white border-2 border-[#0C2C39] shadow-[4px_4px_0px_0px_#0C2C39] rounded-[2rem] p-8 sm:p-12 mb-12 relative overflow-hidden">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="relative z-10 mb-12">
        <h2 className="text-4xl sm:text-5xl font-black text-[#0C2C39] mb-4 tracking-tighter font-roboto">
          Skills & Expertise
        </h2>
        <p className="text-[#0C2C39]/50 text-lg font-medium">Technical competencies and professional skills</p>
      </div>

      {/* Category Filter - Desktop */}
      <div className="hidden md:flex flex-wrap gap-3 mb-12">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all duration-300 border-2 ${activeCategory === category.id
              ? 'bg-[#0C2C39] text-white border-[#0C2C39] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.1)]'
              : 'bg-white text-[#0C2C39]/60 hover:text-[#0C2C39] border-[#0C2C39]/5 hover:border-[#0C2C39]/20'
              }`}
          >
            <Icon name={category.icon as any} size={14} />
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Category Filter - Mobile */}
      <div className="md:hidden mb-10">
        <select
          value={activeCategory}
          onChange={(e) => setActiveCategory(e.target.value)}
          className="w-full px-4 py-3 bg-white border-2 border-[#0C2C39] rounded-xl text-[#0C2C39] font-black uppercase tracking-widest text-[10px] focus:outline-none"
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
        {filteredSkills.map((skill) => {
          const percentage = (skill.proficiency / 5) * 100;

          return (
            <div
              key={skill.id}
              className="group neubrutal-card bg-white p-6 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#0C2C39]/5 border-2 border-[#0C2C39]/10 flex items-center justify-center p-3 group-hover:bg-[#0C2C39] group-hover:text-white transition-all duration-300">
                    {skill.logoUrl ? (
                      <img src={skill.logoUrl} alt={skill.name} className="w-full h-full object-contain" />
                    ) : (
                      <Icon name={skill.icon as any} size={24} />
                    )}
                  </div>
                  <div>
                    <h3 className="font-black text-[#0C2C39] text-lg tracking-tight">{skill.name}</h3>
                    <p className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-widest">{skill.category}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-end">
                  <div className="flex flex-col">
                    <span className="text-[8px] font-black uppercase tracking-widest text-[#0C2C39]/40 mb-1">Expertise</span>
                    <span className="text-xs font-black text-teal-700 uppercase tracking-tighter">
                      {getProficiencyText(skill.proficiency)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-[8px] font-black uppercase tracking-widest text-[#0C2C39]/40 mb-1 block">Experience</span>
                    <span className="text-xs font-black text-[#0C2C39]">{skill.yearsOfExperience}Y+</span>
                  </div>
                </div>

                {/* Modern Progress Bar */}
                <div className="relative h-2.5 bg-[#0C2C39]/5 rounded-sm overflow-hidden border border-[#0C2C39]/5">
                  <div
                    className="absolute top-0 left-0 h-full bg-[#0C2C39] transition-all duration-1000 ease-out"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredSkills.length === 0 && (
        <div className="text-center py-20">
          <Icon name="Search" size={48} className="text-[#0C2C39]/10 mx-auto mb-4" />
          <p className="text-[#0C2C39]/40 font-black uppercase tracking-widest text-xs">No skills found in this category</p>
        </div>
      )}
    </section>
  );
};

export default SkillsMatrix;